import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  Timestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import Window from "../../components/Window";
import Icon, { IconTile } from "../../components/Icon";
import { getUsername, pacedAdd, postAnonymousAd, stripEmoji, withPosterNames } from "../../penpal";
import { useBlocks } from "../../blocks";
import { markBoardSeen } from "../../notifications";
import { POST_PROMPTS } from "../../prompts";
import { usePins } from "../../pins";
import PinButton from "../../components/PinButton";
import { ComposedPost, PostComposer, Postcard, toPlainText } from "../../components/Postcards";
import { useAuthState } from "react-firebase-hooks/auth";
import { db, auth } from "../../firebase";

type Ad = {
  id: string;
  uid?: string; // absent on anonymous ads
  username: string;
  anonymous?: boolean;
  subject: string;
  message: string;
  timestamp: Timestamp;
  color?: string;
  categories?: string[];
  cw?: string; // optional content warning
};

const categoryOptions = [
  "Just Friends 💕",
  "Art Swap 🎨",
  "Story Trade 📖",
  "Advice Needed 🧠",
  "Venting 😮‍💨",
  "Queer Joy 🌈",
  "Witchy + Spiritual 🔮",
  "Neurodivergent Connections 🧠✨",
  "Trans + GNC Solidarity ⚧️",
  "BIPOC Uplift ✊🏽",
  "Mental Health Check-In 🧸",
  "Disability + Spoonie Support 🦽💜",
  "Creative Collab 🎭",
  "Gaming Buddies 🎮",
  "Bookworms + Writers 📚",
  "Nature + Hikes 🏕️",
  "Fandom Friends 🌌",
  "Cozy Interests 🧶",
  "Music Lovers 🎵",
  "Healing + Recovery 🪷",
  "Recovery Support 🩹",
  "Zine + DIY Culture 📓",
  "Alt/Punk/Goth Connections 🖤",
  "Activism + Mutual Aid ✊🏿",
  "Queer Nerds Unite 🤓🏳️‍🌈",
  "Trans Masc Space 🧢",
  "Trans Femme Space 💅",
  "Cottagecore + Soft Life 🍄",
  "Witchcraft + Divination 🕯️",
  "Queer Romance + Flirt 💌",
  "Community Builders 🏘️",
  "Queer Elders + Mentorship 🌻",
  "Student Life 📓",
  "Pet + Animal Lovers 🐾",
];

export default function PenPalBoard() {
  const [ads, setAds] = useState<Ad[]>([]);
  const [user] = useAuthState(auth);
  const { ids: blocked } = useBlocks();
  const pins = usePins(user?.uid);
  const visibleAds = ads.filter((ad) => !ad.uid || !blocked.has(ad.uid));
  const pinnedAds = visibleAds.filter((ad) => pins.includes(ad.id));

  // Search and category filter (everything is already loaded, so it's instant).
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const term = search.trim().toLowerCase();
  const shownAds = visibleAds.filter(
    (ad) =>
      (!category || ad.categories?.includes(category)) &&
      (!term ||
        [ad.subject, toPlainText(ad.message), ad.anonymous ? "" : ad.username, ...(ad.categories || [])]
          .join(" ")
          .toLowerCase()
          .includes(term))
  );
  const filtering = !!term || !!category;

  const renderCard = (ad: Ad) => (
    <Postcard
      key={ad.id}
      to={`/ad/${ad.id}`}
      subject={ad.subject}
      message={ad.message}
      cw={ad.cw}
      color={ad.color}
      categories={ad.categories}
      from={ad.anonymous ? "— a secret admirer ✿" : `— @${ad.username}`}
      corner={user && <PinButton uid={user.uid} adId={ad.id} pinned={pins.includes(ad.id)} />}
    />
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAds();
  }, []);

  // Opening the board (and leaving it) clears the "new posts" badge.
  useEffect(() => {
    if (!user) return;
    markBoardSeen(user.uid);
    return () => markBoardSeen(user.uid);
  }, [user]);

  const fetchAds = async () => {
    setLoading(true);
    try {
      const adsQuery = query(
        collection(db, "penpalAds"),
        orderBy("timestamp", "desc")
      );
      const querySnapshot = await getDocs(adsQuery);
      const adsList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...(doc.data() as Omit<Ad, "id">),
      }));
      setAds(await withPosterNames(adsList));
    } catch (err) {
      console.error("Failed to load ads:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePostAd = async (p: ComposedPost) => {
    if (!user) return;
    const content = {
      subject: p.subject,
      message: p.message,
      timestamp: Timestamp.now(),
      color: p.color,
      categories: p.categories,
      ...(p.cw ? { cw: p.cw } : {}),
    };
    if (p.anonymous) {
      await postAnonymousAd(user.uid, content);
    } else {
      // A signed post always carries your real username.
      const username = await getUsername(user.uid);
      if (username === "unknown") {
        alert("Please choose a username on My Profile first (or post anonymously).");
        return;
      }
      await pacedAdd(user.uid, "post", "penpalAds", { ...content, uid: user.uid, username });
    }
    fetchAds();
  };

  return (
    <>
      <Window title="penpal_board.htm">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="window-heading flex items-center gap-3">
              <IconTile name="pin" tone="blue" size={44} /> The PenPal Board
            </h1>
            <p className="window-sub !mb-0">
              Pin up a letter, or answer someone else's.
            </p>
          </div>
          <Link to="/board/directory" className="btn btn-plain btn-sm">
            <Icon name="book" size={16} /> Pen Pal Directory
          </Link>
        </div>
      </Window>

      {user && (
        <Window title="write_a_letter.exe" tone="blue">
          <details>
            <summary className="btn btn-blue cursor-pointer list-none">
              <Icon name="edit" size={16} /> Post an ad
            </summary>

            <PostComposer
              titleLabel="Ad title"
              titlePlaceholder="e.g. Seeking a fellow tea + zine nerd"
              bodyPlaceholder="Tell folks about yourself and the kind of pen pal you're hoping for..."
              categoryOptions={categoryOptions}
              anonymousHint="Your username won't appear on this ad, and it isn't linked to your profile. Letters, kudos, and requests still reach your mailbox. They only learn who you are if you write back or accept their request."
              submitLabel="Pin it to the board"
              onPost={handlePostAd}
              ideas={POST_PROMPTS}
            />
          </details>
        </Window>
      )}

      {pinnedAds.length > 0 && (
        <Window title={`pinned (${pinnedAds.length})`} tone="lavender">
          <p className="mb-3 text-sm text-ink-soft">
            Your favorite posts. Only you can see your pins.
          </p>
          <div className="grid gap-5 pt-2 sm:grid-cols-2 xl:grid-cols-3">
            {pinnedAds.map(renderCard)}
          </div>
        </Window>
      )}

      {!loading && visibleAds.length > 0 && (
        <div className="flex flex-wrap items-end gap-3" role="search">
          <label className="field min-w-[12rem] flex-1">
            <span className="field-label">Search the board</span>
            <input
              type="search"
              className="input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="zines, tarot, spoonie, a username..."
            />
          </label>
          <label className="field min-w-[12rem]">
            <span className="field-label">Category</span>
            <select className="input" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">All categories</option>
              {categoryOptions.map((cat) => (
                <option key={cat} value={cat}>
                  {stripEmoji(cat)}
                </option>
              ))}
            </select>
          </label>
          {filtering && (
            <button
              type="button"
              className="btn btn-plain btn-sm"
              onClick={() => {
                setSearch("");
                setCategory("");
              }}
            >
              Clear
            </button>
          )}
          {filtering && (
            <p className="w-full text-sm text-ink-soft" role="status">
              {shownAds.length} of {visibleAds.length} posts
            </p>
          )}
        </div>
      )}

      {loading ? (
        <p className="empty">Fetching the mail...</p>
      ) : filtering && shownAds.length === 0 ? (
        <div className="window">
          <p className="empty">Nothing matches that yet. Try another word or category?</p>
        </div>
      ) : visibleAds.length === 0 ? (
        <div className="window">
          <p className="empty">
            <span className="empty-icon">
              <Icon name="mail" size={36} tone="pink" pop />
            </span>
            No ads yet. Be the first to pin one up!
          </p>
        </div>
      ) : (
        <div className="grid gap-5 pt-2 sm:grid-cols-2 xl:grid-cols-3">
          {shownAds.map(renderCard)}
        </div>
      )}
    </>
  );
}
